/**
 * The orca v3 catalog: every condition kind, action and input a loop may name, and the
 * connectors that supply them.
 *
 * The core knows only its own conditions — the repo's checks, a clean merge, and the two
 * fresh reviews. Every outside system is a connector: it supplies inputs it can fetch,
 * actions the runtime may take in it, and conditions it can check by re-reading its own
 * state. GitHub and Linear are the first two; GitLab and Figma fit the same shape (a code
 * host with change requests; a design source whose version and dev status are
 * checkable). Families let a loop's conditions stay host-neutral: `change_request.ci_green`
 * means a GitHub rollup or a GitLab pipeline, depending on the workspace's connector.
 *
 * @since 1.0.0
 */
import { Option } from "effect";
import type { ActionPhase, Decider, LoopDefinition, LoopMode } from "./model.ts";

/**
 * A capability family a connector implements.
 *
 * @since 1.0.0
 */
export type ConnectorFamily = "code_host" | "change_request" | "issue_tracker" | "design_source";

/**
 * One named parameter of a condition or action.
 *
 * @since 1.0.0
 */
export interface ParamSpec {
  readonly name: string;
  readonly type: "string" | "number" | "boolean";
  readonly required: boolean;
  readonly description: string;
}

/**
 * A kind of condition.
 *
 * @since 1.0.0
 */
export interface ConditionKind {
  readonly kind: string;
  readonly title: string;
  readonly by: Decider;
  readonly description: string;
  /** Checked on a commit of the run's code workspace. */
  readonly needsWorkspace: boolean;
  readonly family?: ConnectorFamily;
  readonly params: ReadonlyArray<ParamSpec>;
}

/**
 * An action the runtime can take in a connector's system.
 *
 * Destructive actions (a merge, moving an issue to done) run as steps, by the runtime.
 * Non-destructive ones may also be granted to the orchestrator's `take_action` tool
 * (`during`); a destructive one only when it has a `grantLimit` the grant pins.
 *
 * @since 1.0.0
 */
export interface ActionKind {
  readonly action: string;
  readonly title: string;
  readonly description: string;
  readonly destructive: boolean;
  readonly phases: ReadonlyArray<ActionPhase>;
  /** The condition kind that re-reads the system to confirm the action took effect. */
  readonly verify?: string;
  readonly params: ReadonlyArray<ParamSpec>;
  /**
   * The param a grant must pin (in `access[].limits`) before the orchestrator may take this
   * destructive action during a run.
   */
  readonly grantLimit?: string;
  /** Whether `take_action` must wait until every runtime and reviewer condition is met on the head. */
  readonly waitsForConditions: boolean;
}

/**
 * Something a connector can fetch as a run input, snapshotted at run start.
 *
 * @since 1.0.0
 */
export interface InputKind {
  readonly input: string;
  readonly title: string;
  readonly description: string;
}

/**
 * An outside event that starts a run of a loop by itself.
 *
 * @since 1.0.0
 */
export interface TriggerKind {
  /** e.g. `issue.state`, `change_request.updated`. */
  readonly on: string;
  readonly title: string;
  readonly description: string;
  /** The connector input kind the event fills; a loop with this trigger must declare such an input. */
  readonly input: string;
  /** Fires on the loop's workspace repo, so the loop needs a workspace hosted by this connector. */
  readonly needsWorkspace: boolean;
  readonly params: ReadonlyArray<ParamSpec>;
}

/**
 * A connector's vendor MCP server, which the loop host may proxy to agents.
 *
 * @since 1.0.0
 */
export interface VendorMcp {
  readonly url: string;
  /** The read-only variant, when the vendor offers one. */
  readonly readonlyUrl?: string;
  /** Usable by a server-side process with a token, no browser. */
  readonly headless: boolean;
}

/**
 * One outside system.
 *
 * @since 1.0.0
 */
export interface ConnectorSpec {
  readonly id: string;
  readonly title: string;
  readonly families: ReadonlyArray<ConnectorFamily>;
  readonly inputs: ReadonlyArray<InputKind>;
  readonly conditions: ReadonlyArray<ConditionKind>;
  readonly actions: ReadonlyArray<ActionKind>;
  /** The events that start a run by themselves. */
  readonly triggers: ReadonlyArray<TriggerKind>;
  /** Hosts a git workspace the run can clone and push. */
  readonly workspace: boolean;
  /** Whether its action steps default to `required`. Linear's never block (best-effort). */
  readonly requiredByDefault: boolean;
  readonly mcp?: VendorMcp;
}

const param = (name: string, type: ParamSpec["type"], required: boolean, description: string): ParamSpec => ({
  name,
  type,
  required,
  description,
});

/**
 * The conditions the runtime decides itself, independent of any connector.
 *
 * @since 1.0.0
 */
export const CORE_CONDITIONS: ReadonlyArray<ConditionKind> = [
  {
    kind: "workspace.checks",
    title: "Repo checks pass",
    by: "runtime",
    description:
      "The repo's own checks (its orca.json contract: type check, lint, tests with coverage) pass on the change head.",
    needsWorkspace: true,
    params: [param("parts", "string", false, "Comma-separated subset of the repo's check parts; default all")],
  },
  {
    kind: "workspace.no_conflicts",
    title: "No merge conflicts",
    by: "runtime",
    description: "The change head merges cleanly into the base branch as it is now.",
    needsWorkspace: true,
    params: [],
  },
  {
    kind: "review.completion",
    title: "Completion review passes",
    by: "reviewer",
    description: "A fresh reviewer that never saw the work finds every requirement of the brief met.",
    needsWorkspace: true,
    params: [],
  },
  {
    kind: "review.code",
    title: "Code review passes",
    by: "reviewer",
    description: "A fresh reviewer finds no defect it would block a merge on.",
    needsWorkspace: true,
    params: [],
  },
];

/**
 * The two generic kinds a flexible definition's conditions run as on the catalog host: each
 * carries its loop-specific condition whole in `params.flexible`, so neither declares params.
 *
 * @since 1.0.0
 */
export const FLEXIBLE_CONDITIONS: ReadonlyArray<ConditionKind> = [
  {
    kind: "flexible.script",
    title: "Pinned script passes",
    by: "runtime",
    description:
      "The pinned script of the flexible condition in params.flexible runs on the change head and meets its success criteria.",
    needsWorkspace: true,
    params: [],
  },
  {
    kind: "flexible.judge",
    title: "Judge finds the condition met",
    by: "reviewer",
    description:
      "A fresh judge finds every success criterion of the flexible condition in params.flexible met on the change head.",
    needsWorkspace: true,
    params: [],
  },
];

const CHANGE_REQUEST_CONDITIONS: ReadonlyArray<ConditionKind> = [
  {
    kind: "change_request.open",
    title: "Change request open",
    by: "connector",
    family: "change_request",
    description: "A change request (pull or merge request) is open for the change branch, showing its current head.",
    needsWorkspace: true,
    params: [],
  },
  {
    kind: "change_request.ci_green",
    title: "CI green",
    by: "connector",
    family: "change_request",
    description: "The host's CI for the change request's head has finished and passed.",
    needsWorkspace: true,
    params: [],
  },
  {
    kind: "change_request.mergeable",
    title: "Mergeable",
    by: "connector",
    family: "change_request",
    description: "The host reports the change request can merge as it stands.",
    needsWorkspace: true,
    params: [],
  },
  {
    kind: "change_request.merged",
    title: "Merged",
    by: "connector",
    family: "change_request",
    description: "The change request is merged into the base branch.",
    needsWorkspace: true,
    params: [],
  },
];

const CHANGE_REQUEST_ACTIONS: ReadonlyArray<ActionKind> = [
  {
    action: "change_request.open",
    title: "Open the change request",
    description: "Push the change branch and open (or update) its change request.",
    destructive: false,
    phases: ["during", "after"],
    verify: "change_request.open",
    params: [param("summary", "string", false, "What changed and why, for the description")],
    waitsForConditions: true,
  },
  {
    action: "change_request.merge",
    title: "Merge",
    description: "Merge the change request, guarded by the head the conditions passed on.",
    destructive: true,
    phases: ["after"],
    verify: "change_request.merged",
    params: [param("method", "string", false, "merge, squash or rebase; default squash")],
    waitsForConditions: false,
  },
];

/**
 * The GitHub input kind its triggers fill: a change request head, or a pushed branch head.
 *
 * @since 1.0.0
 */
export const CHANGE_REQUEST_INPUT = "change_request";

/**
 * GitHub's trigger events, in catalog order.
 *
 * @since 1.0.0
 */
export const GITHUB_TRIGGER_EVENTS = ["change_request.updated", "branch.pushed"] as const;
export type GitHubTriggerEvent = (typeof GITHUB_TRIGGER_EVENTS)[number];

/**
 * @since 1.0.0
 */
export const findTriggerKind = (connector: string, on: string): Option.Option<TriggerKind> =>
  findConnector(connector).pipe(Option.flatMap((c) => Option.fromNullable(c.triggers.find((t) => t.on === on))));

/**
 * The definition's mode; absent is `build`.
 *
 * @since 1.0.0
 */
export const loopModeOf = (definition: LoopDefinition): LoopMode => definition.mode ?? "build";

/**
 * GitHub: a code host whose pull requests, checks and merges are the change-request family.
 *
 * @since 1.0.0
 */
export const GITHUB: ConnectorSpec = {
  id: "github",
  title: "GitHub",
  families: ["code_host", "change_request"],
  inputs: [{
    input: CHANGE_REQUEST_INPUT,
    title: "Change request",
    description: "A pull request head, or a pushed branch head",
  }],
  conditions: CHANGE_REQUEST_CONDITIONS,
  actions: CHANGE_REQUEST_ACTIONS,
  triggers: [
    {
      on: "change_request.updated",
      title: "Change request opened or updated",
      description:
        "A pull request into any base branch of the workspace's repo (main, staging, project/…) is opened, reopened, or gets a new head commit.",
      input: CHANGE_REQUEST_INPUT,
      needsWorkspace: true,
      params: [],
    },
    {
      on: "branch.pushed",
      title: "Branch pushed",
      description: "A push moves a branch of the workspace's repo to a new head commit.",
      input: CHANGE_REQUEST_INPUT,
      needsWorkspace: true,
      params: [param("branch", "string", false, "The branch; default the workspace's base")],
    },
  ],
  workspace: true,
  requiredByDefault: true,
  mcp: {
    url: "https://api.githubcopilot.com/mcp/",
    readonlyUrl: "https://api.githubcopilot.com/mcp/readonly",
    headless: true,
  },
};

/**
 * Linear: an issue tracker. An issue, or a project with its issues, can be a run's brief,
 * "the issue is completed" a condition, and moving or commenting on it an action. Never
 * blocking by default.
 *
 * @since 1.0.0
 */
export const LINEAR: ConnectorSpec = {
  id: "linear",
  title: "Linear",
  families: ["issue_tracker"],
  inputs: [{
    input: "issue",
    title: "Issue",
    description: "An issue's title, description and labels, used as the brief",
  }, {
    input: "project",
    title: "Project",
    description: "A Linear project: its name, description, and issues with their blocks relations",
  }],
  conditions: [
    {
      kind: "issue.completed",
      title: "Issue completed",
      by: "connector",
      family: "issue_tracker",
      description: "The issue's workflow state is of type completed.",
      needsWorkspace: false,
      params: [param("issue", "string", false, "Issue identifier; default the run's issue input")],
    },
    {
      kind: "project.completed",
      title: "Project completed",
      by: "connector",
      family: "issue_tracker",
      description: "Every issue in the project (canceled and duplicate excluded) is in a completed state.",
      needsWorkspace: false,
      params: [param("project", "string", false, "Project id; default the run's project input")],
    },
  ],
  actions: [
    {
      action: "issue.set_state",
      title: "Move the issue",
      description:
        "Move an issue (default the run's) to a workflow state, given by exactly one of its type (`state`: started, completed, …) or its name (`name`).",
      destructive: true,
      phases: ["before", "during", "after", "if_unmet"],
      // No default verify: moving to "started" is not verified by "issue.completed". The
      // step's own `verify` decides (ship-a-change's completed step names it).
      params: [
        param("state", "string", false, "A workflow state type: started, completed, unstarted, backlog"),
        param("name", "string", false, "A workflow state's name, matched exactly (case-sensitive)"),
        param("issue", "string", false, "Issue identifier; default the run's issue input"),
      ],
      grantLimit: "name",
      waitsForConditions: false,
    },
    {
      action: "issue.comment",
      title: "Comment on the issue",
      description: "Post a comment, e.g. the run's link when it starts, or why it stopped.",
      destructive: false,
      phases: ["before", "during", "after", "if_unmet"],
      params: [param("body", "string", false, "Markdown; default a line with the run's link and status")],
      waitsForConditions: false,
    },
  ],
  triggers: [{
    on: "issue.state",
    title: "Issue enters a state",
    description: "An issue moves into the workflow state whose name is exactly `state` (case-sensitive).",
    input: "issue",
    needsWorkspace: false,
    params: [param("state", "string", true, "The workflow state's name, matched exactly")],
  }],
  workspace: false,
  requiredByDefault: false,
  mcp: { url: "https://mcp.linear.app/mcp", readonlyUrl: "https://mcp.linear.app/mcp/readonly", headless: true },
};

/**
 * Every connector this build knows.
 *
 * @since 1.0.0
 */
export const CONNECTORS: ReadonlyArray<ConnectorSpec> = [GITHUB, LINEAR];

/**
 * @since 1.0.0
 */
export const findConnector = (id: string): Option.Option<ConnectorSpec> =>
  Option.fromNullable(CONNECTORS.find((c) => c.id === id));

/**
 * A condition kind: a core or flexible kind when `connector` is absent, else one of that connector's.
 *
 * @since 1.0.0
 */
export const findConditionKind = (kind: string, connector?: string): Option.Option<ConditionKind> =>
  connector === undefined
    ? Option.fromNullable(
      CORE_CONDITIONS.find((c) => c.kind === kind) ?? FLEXIBLE_CONDITIONS.find((c) => c.kind === kind),
    )
    : findConnector(connector).pipe(
      Option.flatMap((c) => Option.fromNullable(c.conditions.find((k) => k.kind === kind))),
    );

/**
 * @since 1.0.0
 */
export const findActionKind = (connector: string, action: string): Option.Option<ActionKind> =>
  findConnector(connector).pipe(Option.flatMap((c) => Option.fromNullable(c.actions.find((a) => a.action === action))));

/**
 * @since 1.0.0
 */
export const findInputKind = (connector: string, input: string): Option.Option<InputKind> =>
  findConnector(connector).pipe(Option.flatMap((c) => Option.fromNullable(c.inputs.find((i) => i.input === input))));

/**
 * The connectors a definition touches, in first-mention order — what the loops list
 * groups by and the run page lists as "Connected".
 *
 * @since 1.0.0
 */
export const connectorsOf = (definition: LoopDefinition): ReadonlyArray<string> => {
  const ids = [
    ...definition.inputs.flatMap((i) => (i.connector === undefined ? [] : [i.connector])),
    ...(definition.workspace === undefined ? [] : [definition.workspace.connector]),
    ...definition.conditions.flatMap((c) => (c.connector === undefined ? [] : [c.connector])),
    ...[...definition.before, ...definition.after, ...definition.ifUnmet].map((s) => s.connector),
    ...definition.access.map((a) => a.connector),
  ];
  return ids.filter((id, i) => ids.indexOf(id) === i);
};

/**
 * The "ship a change" loop's starting playbook: short numbered habits for the orchestrator —
 * size and split the work, read each summary, recover unlanded work instead of redoing it,
 * stop on an environment fault instead of re-rolling it, and finish only when every
 * condition holds. Generic to any repo: it names no repo-specific command.
 *
 * @since 1.0.0
 */
export const SHIP_A_CHANGE_PLAYBOOK: string = [
  "1. Size the work first. When it is big, split it into pieces that touch disjoint files and start one subagent per piece, in parallel up to the subagent limit. A small change is one subagent.",
  "2. When a subagent finishes, read its summary before deciding the next move: what it changed, what it left undone, and whether its work landed.",
  "3. Recover finished work that did not land (a landing refused on a runtime error; `status` lists it) — never redo it. Start a subagent with `continue_from` set to that agent, told to finish what is there so the runtime lands it. Never start a fresh subagent on the same task.",
  "4. A check that fails with no error from the code — the process was killed, ran out of memory or hit its time limit, and its result says `cause: \"environment\"` — is an environment fault. Don't start a subagent to diagnose it, and never commit a filler change to make a check run again: it fails the same way. Call `ask_person` with the evidence (the part, the signal or limit, the log lines). If it answers that no one is available, don't keep going, whatever the answer says: end your turn with that evidence in your final message.",
  "5. Call `finish` only when every condition holds on the current head. Never call it while a condition is failing or pending.",
].join("\n");

/**
 * The "ship a change" loop: take a brief (or a Linear issue) to a change merged into a
 * GitHub repo's base branch.
 *
 * Conditions run cheapest first: the repo's checks and a clean merge, then the two fresh
 * reviews on the same head, then the host's CI and mergeability on the change request the
 * orchestrator opens. Once all hold, the runtime merges (verified by re-reading the pull
 * request) and only then moves the issue to completed, so the issue is never done while
 * the code isn't in.
 *
 * @since 1.0.0
 */
export const shipAChange = (options: {
  readonly repo: string;
  readonly base: string;
  readonly fromIssue: boolean;
  readonly merge: boolean;
}): LoopDefinition => ({
  inputs: options.fromIssue
    ? [{ id: "issue", title: "Linear issue", kind: "connector", connector: "linear", input: "issue", required: true }]
    : [{ id: "brief", title: "Brief", kind: "text", required: true }],
  workspace: { connector: "github", repo: options.repo, base: options.base },
  conditions: [
    { id: "checks", kind: "workspace.checks", by: "runtime" },
    { id: "no_conflicts", kind: "workspace.no_conflicts", by: "runtime" },
    { id: "completion", kind: "review.completion", by: "reviewer", after: ["checks", "no_conflicts"] },
    { id: "code_review", kind: "review.code", by: "reviewer", after: ["checks", "no_conflicts"] },
    {
      id: "ci",
      kind: "change_request.ci_green",
      by: "connector",
      connector: "github",
      after: ["completion", "code_review"],
    },
    { id: "mergeable", kind: "change_request.mergeable", by: "connector", connector: "github", after: ["ci"] },
  ],
  before: options.fromIssue
    ? [
      { connector: "linear", action: "issue.set_state", params: { state: "started" }, required: false },
      { connector: "linear", action: "issue.comment", required: false },
    ]
    : [],
  after: [
    ...(options.merge
      ? [{
        connector: "github",
        action: "change_request.merge",
        required: true,
        verify: { kind: "change_request.merged" },
      }]
      : []),
    ...(options.fromIssue && options.merge
      ? [{
        connector: "linear",
        action: "issue.set_state",
        params: { state: "completed" },
        required: false,
        verify: { kind: "issue.completed" },
      }]
      : []),
  ],
  ifUnmet: options.fromIssue ? [{ connector: "linear", action: "issue.comment", required: false }] : [],
  budget: { usd: 25, minutes: 120 },
  parallel: 3,
  playbook: SHIP_A_CHANGE_PLAYBOOK,
  access: [
    {
      connector: "github",
      mcp: { readonly: true, toolsets: ["repos", "pull_requests", "actions"] },
      actions: ["change_request.open"],
    },
    ...(options.fromIssue
      ? [{ connector: "linear", mcp: { readonly: true, toolsets: [] }, actions: ["issue.comment"] }]
      : []),
  ],
});
